<template>
  <div class="app-container work-log-received-page">
    <el-form ref="queryForm" :model="queryParams" v-show="showSearch" label-width="86px">
      <el-row :gutter="10" class="mb8">
        <el-col :xs="24" :md="12" :lg="6">
          <el-form-item label="日志模板" prop="templateId">
            <el-select v-model="queryParams.templateId" placeholder="请选择模板" clearable filterable style="width:100%">
              <el-option v-for="item in templateOptions" :key="item.id" :label="filterTemplateName(item)" :value="item.id" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :xs="24" :md="12" :lg="6">
          <el-form-item label="填写人" prop="submitUserId">
            <el-select v-model="queryParams.submitUserId" placeholder="请选择填写人" clearable filterable style="width:100%">
              <el-option v-for="user in staffOptions" :key="user.userId" :label="user.nickName" :value="user.userId" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :xs="24" :md="12" :lg="8">
          <el-form-item label="日志日期" prop="dateRange">
            <el-date-picker
              v-model="queryParams.dateRange"
              value-format="yyyy-MM-dd"
              type="daterange"
              range-separator="-"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              style="width:100%"
            />
          </el-form-item>
        </el-col>
        <el-col :xs="24" :md="12" :lg="6">
          <el-form-item label="关键词" prop="keyword">
            <el-input v-model="queryParams.keyword" placeholder="标题/内容关键词" clearable @keyup.enter.native="handleQuery" />
          </el-form-item>
        </el-col>
        <el-col :xs="24" :md="24" :lg="24" align="right">
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="handleQuery">搜索</el-button>
            <el-button icon="el-icon-refresh" @click="resetQuery">清空</el-button>
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>

    <el-row :gutter="10" class="mb8">
      <right-toolbar :showSearch.sync="showSearch" @queryTable="getList"></right-toolbar>
    </el-row>

    <el-table v-loading="loading" :data="dataList" height="calc(100vh - 260px)" class="work-log-table">
      <el-table-column label="日志" min-width="360" fixed="left">
        <template slot-scope="scope">
          <div class="log-title-cell">
            <div class="log-row-title">{{ displayLogTitle(scope.row) }}</div>
            <div class="log-row-meta">
              <el-tag size="mini" effect="plain">{{ displayLogTemplateName(scope.row) }}</el-tag>
              <span>{{ periodText(scope.row) }}</span>
            </div>
            <div class="log-row-preview">{{ contentPreview(scope.row) }}</div>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="填写人" prop="submitUserName" width="120" align="center" />
      <el-table-column label="发送给" prop="receiverSummary" min-width="160" show-overflow-tooltip />
      <el-table-column label="提交时间" width="170" align="center">
        <template slot-scope="scope">{{ displayMinuteTime(scope.row.submitTime) }}</template>
      </el-table-column>
      <el-table-column label="操作" width="100" fixed="right" align="center" class-name="small-padding fixed-width">
        <template slot-scope="scope">
          <el-button type="text" @click="handleDetail(scope.row)">详情</el-button>
        </template>
      </el-table-column>
    </el-table>

    <pagination v-show="total>0" :total="total" :page.sync="queryParams.pageNum" :limit.sync="queryParams.pageSize" @pagination="getList" />

    <work-log-detail-dialog :visible.sync="detailOpen" :log-id="detailId" @refresh="getList" />
  </div>
</template>

<script>
import { listReceivedWorkLogs } from '@/api/crm/workLog'
import { listWorkLogTemplateOptions } from '@/api/crm/workLogTemplate'
import { listUserWithOutDataScope } from '@/api/system/user'
import WorkLogDetailDialog from './components/detailDialog'

const ACTIVE_TENANT_USER_QUERY = { pageNum: 1, pageSize: 999, tenantUserStatus: 1 }
const BLANK_TEMPLATE_CODE = 'BLANK_LOG'
const BLANK_FILTER_NAME = '其他'
const BLANK_LOG_DISPLAY_NAME = '其他日志'

export default {
  name: 'WorkLogReceived',
  components: {
    WorkLogDetailDialog
  },
  data() {
    return {
      loading: false,
      showSearch: true,
      total: 0,
      dataList: [],
      templateOptions: [],
      staffOptions: [],
      detailOpen: false,
      detailId: null,
      queryParams: {
        pageNum: 1,
        pageSize: 10,
        templateId: null,
        submitUserId: null,
        keyword: '',
        dateRange: []
      }
    }
  },
  created() {
    this.loadTemplateOptions()
    this.loadUsers()
    this.getList()
  },
  methods: {
    isBlankTemplate(item) {
      if (!item) {
        return false
      }
      return item.templateCode === BLANK_TEMPLATE_CODE || item.templateName === '空白日志'
    },
    filterTemplateName(item) {
      return this.isBlankTemplate(item) ? BLANK_FILTER_NAME : (item.templateName || '未命名模板')
    },
    isBlankLog(row) {
      return row && (row.templateCode === BLANK_TEMPLATE_CODE || row.templateNameSnapshot === '空白日志' || row.templateName === '空白日志')
    },
    displayLogTitle(row) {
      const title = (row && row.title) || '未命名日志'
      return this.isBlankLog(row) ? title.replace(/空白日志/g, BLANK_LOG_DISPLAY_NAME) : title
    },
    displayLogTemplateName(row) {
      return this.isBlankLog(row) ? BLANK_FILTER_NAME : ((row && (row.templateNameSnapshot || row.templateName)) || '-')
    },
    loadTemplateOptions() {
      listWorkLogTemplateOptions().then(response => {
        this.templateOptions = (response && response.data) || []
      })
    },
    loadUsers() {
      listUserWithOutDataScope(ACTIVE_TENANT_USER_QUERY).then(response => {
        this.staffOptions = (response && response.rows) || []
      })
    },
    getList() {
      this.loading = true
      listReceivedWorkLogs(this.buildQueryParams())
        .then(response => {
          this.dataList = (response && response.rows) || []
          this.total = (response && response.total) || 0
        })
        .finally(() => {
          this.loading = false
        })
    },
    buildQueryParams() {
      const params = { ...this.queryParams, status: 2 }
      if (params.dateRange && params.dateRange.length > 1) {
        params.beginDate = params.dateRange[0]
        params.endDate = params.dateRange[1]
      }
      delete params.dateRange
      return this.cleanParams(params)
    },
    handleQuery() {
      this.queryParams.pageNum = 1
      this.getList()
    },
    resetQuery() {
      this.queryParams = {
        pageNum: 1,
        pageSize: 10,
        templateId: null,
        submitUserId: null,
        keyword: '',
        dateRange: []
      }
      this.getList()
    },
    handleDetail(row) {
      this.detailId = row.id
      this.detailOpen = true
    },
    periodText(row) {
      if (row.logType === 'WEEKLY') {
        return [row.periodStartDate, row.periodEndDate].filter(Boolean).join(' 至 ') || '-'
      }
      return row.periodDate || '-'
    },
    contentPreview(row) {
      const text = String((row && row.plainContent) || '').replace(/\s+/g, ' ').trim()
      if (!text) {
        return '暂无内容摘要'
      }
      return text.length > 86 ? `${text.slice(0, 86)}...` : text
    },
    displayMinuteTime(value) {
      if (!value) {
        return '-'
      }
      return String(value).replace('T', ' ').slice(0, 16)
    },
    cleanParams(params) {
      const result = {}
      Object.keys(params || {}).forEach(key => {
        const value = params[key]
        if (value !== '' && value !== null && value !== undefined) {
          result[key] = value
        }
      })
      return result
    }
  }
}
</script>

<style scoped>
.work-log-received-page {
  background: #fff;
}

.work-log-table ::v-deep .el-table__row td {
  padding-top: 10px;
  padding-bottom: 10px;
}

.log-title-cell {
  padding: 2px 0;
}

.log-row-title {
  overflow: hidden;
  color: #1f2937;
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.log-row-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 5px;
  color: #8a95a6;
  font-size: 12px;
  line-height: 20px;
}

.log-row-preview {
  overflow: hidden;
  margin-top: 5px;
  color: #5f6f86;
  font-size: 13px;
  line-height: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
